import { useEffect, useRef, useState, type ReactElement } from 'react';
import { cn } from '@/components/ui/cn';
import type { QuestionId } from '../types';

/**
 * Quiet Atlas backdrop illustrations — one per quiz question.
 * Thin ink strokes (currentColor), no fills, decorative only.
 * Sized as 320×320 viewBoxes so a single CSS scale gives crisp lines.
 */

type BackdropSvg = (props: { className?: string }) => ReactElement;

const baseSvg = (children: React.ReactNode, props?: { className?: string }) => (
  <svg
    viewBox="0 0 320 320"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.1"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={cn('h-full w-full', props?.className)}
  >
    {children}
  </svg>
);

const Horizon: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Sundial gnomon + arc with hour marks */}
      <path d="M40 220 L280 220" strokeDasharray="2 6" opacity="0.55" />
      <path d="M60 220 A100 100 0 0 1 260 220" strokeWidth="0.9" />
      {Array.from({ length: 11 }, (_, i) => i + 1).map((i) => {
        const a = (Math.PI * i) / 12;
        const r1 = 100;
        const r2 = 108;
        const cx = 160;
        const cy = 220;
        const x1 = cx - r1 * Math.cos(a);
        const y1 = cy - r1 * Math.sin(a);
        const x2 = cx - r2 * Math.cos(a);
        const y2 = cy - r2 * Math.sin(a);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth="0.7" />;
      })}
      {/* Gnomon */}
      <path d="M160 220 L160 130" strokeWidth="1.4" />
      <path d="M160 130 L210 220" strokeWidth="0.8" opacity="0.6" />
      {/* Sun glyph upper-right */}
      <circle cx="245" cy="90" r="14" />
      {Array.from({ length: 8 }, (_, i) => i).map((i) => {
        const a = (Math.PI * 2 * i) / 8;
        const x1 = 245 + 20 * Math.cos(a);
        const y1 = 90 + 20 * Math.sin(a);
        const x2 = 245 + 26 * Math.cos(a);
        const y2 = 90 + 26 * Math.sin(a);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth="0.8" />;
      })}
      {/* Arrow forward */}
      <path d="M50 260 L270 260" strokeDasharray="3 5" opacity="0.5" />
      <path d="M260 254 L272 260 L260 266" />
    </>,
  );

const Passport: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Globe arc */}
      <circle cx="120" cy="160" r="68" strokeWidth="0.9" />
      <path d="M52 160 Q120 130 188 160" strokeWidth="0.7" opacity="0.6" />
      <path d="M52 160 Q120 190 188 160" strokeWidth="0.7" opacity="0.6" />
      <path d="M120 92 L120 228" strokeWidth="0.7" opacity="0.6" />
      <path d="M76 116 Q120 160 164 204" strokeWidth="0.7" opacity="0.5" strokeDasharray="2 3" />
      {/* Dashed flight arc */}
      <path d="M60 230 Q170 60 280 200" strokeDasharray="3 6" opacity="0.7" />
      {/* Plane */}
      <g transform="translate(258 120) rotate(35)">
        <path d="M-14 0 L14 0 L18 -2 L14 -4 L-2 -4 L-8 -10 L-12 -10 L-8 -4 L-14 -4 Z" strokeWidth="0.9" />
      </g>
      {/* Stamp circle */}
      <circle cx="240" cy="240" r="26" strokeDasharray="2 3" opacity="0.7" />
      <circle cx="240" cy="240" r="20" strokeWidth="0.6" opacity="0.55" />
      <text
        x="240"
        y="244"
        textAnchor="middle"
        fontFamily="ui-monospace,Menlo,monospace"
        fontSize="9"
        fill="currentColor"
        stroke="none"
        opacity="0.7"
      >
        ENTRY
      </text>
    </>,
  );

const Climate: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Sun upper-left */}
      <circle cx="80" cy="80" r="18" />
      {Array.from({ length: 8 }, (_, i) => i).map((i) => {
        const a = (Math.PI * 2 * i) / 8;
        const x1 = 80 + 24 * Math.cos(a);
        const y1 = 80 + 24 * Math.sin(a);
        const x2 = 80 + 32 * Math.cos(a);
        const y2 = 80 + 32 * Math.sin(a);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth="0.8" />;
      })}
      {/* Cloud middle */}
      <path d="M150 110 q-18 0 -22 14 q-16 2 -16 16 q0 16 18 16 h54 q18 0 18 -16 q0 -14 -16 -16 q-4 -14 -22 -14 q-8 0 -14 6 Z" strokeWidth="0.9" />
      {/* Raindrops */}
      <path d="M148 162 l-4 12" strokeWidth="0.8" />
      <path d="M168 162 l-4 12" strokeWidth="0.8" />
      <path d="M186 162 l-4 12" strokeWidth="0.8" />
      {/* Mountain ridges (alpine) */}
      <path d="M30 250 L90 180 L140 230 L190 170 L240 240 L300 200 L300 270 L30 270 Z" strokeWidth="0.9" />
      <path d="M90 180 L100 195 L110 178" strokeWidth="0.6" />
      <path d="M190 170 L200 185 L210 168" strokeWidth="0.6" />
      {/* Snowflake bottom-right */}
      <g transform="translate(260 90)" strokeWidth="0.8">
        <line x1="-14" y1="0" x2="14" y2="0" />
        <line x1="0" y1="-14" x2="0" y2="14" />
        <line x1="-10" y1="-10" x2="10" y2="10" />
        <line x1="-10" y1="10" x2="10" y2="-10" />
        <path d="M-12 -2 L-14 0 L-12 2" />
        <path d="M12 -2 L14 0 L12 2" />
        <path d="M-2 -12 L0 -14 L2 -12" />
        <path d="M-2 12 L0 14 L2 12" />
      </g>
    </>,
  );

const UrbanDensity: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Skyline of varied buildings */}
      <path d="M30 270 L30 200 L70 200 L70 160 L100 160 L100 220 L130 220 L130 140 L155 140 L155 180 L180 180 L180 120 L210 120 L210 200 L240 200 L240 170 L270 170 L270 240 L295 240 L295 270 Z" />
      {/* Windows pattern */}
      {[
        [40, 215], [40, 230], [40, 245], [55, 215], [55, 230], [55, 245],
        [80, 180], [80, 200], [110, 235], [110, 250],
        [140, 160], [140, 180], [140, 200], [165, 200],
        [190, 145], [190, 165], [190, 185], [220, 215], [220, 240],
        [250, 190], [250, 215], [280, 250],
      ].map(([x, y], i) => (
        <rect key={i} x={x} y={y} width="6" height="6" strokeWidth="0.6" opacity="0.7" />
      ))}
      {/* Subtle horizon line */}
      <path d="M0 270 L320 270" strokeWidth="0.6" opacity="0.5" />
      {/* Far hills behind */}
      <path d="M0 200 Q60 170 130 195 Q200 220 320 190" strokeWidth="0.6" strokeDasharray="2 5" opacity="0.5" />
    </>,
  );

const Budget: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Large coin */}
      <circle cx="115" cy="160" r="64" />
      <circle cx="115" cy="160" r="56" strokeWidth="0.6" opacity="0.6" />
      <text
        x="115"
        y="170"
        textAnchor="middle"
        fontFamily="ui-monospace,Menlo,monospace"
        fontSize="36"
        fill="currentColor"
        stroke="none"
        opacity="0.75"
      >
        €
      </text>
      {/* Smaller coin */}
      <circle cx="210" cy="100" r="28" strokeWidth="0.9" />
      <circle cx="210" cy="100" r="22" strokeWidth="0.5" opacity="0.6" />
      {/* Banknote */}
      <rect x="170" y="190" width="120" height="60" rx="2" strokeWidth="0.9" />
      <circle cx="230" cy="220" r="12" strokeWidth="0.7" />
      <path d="M178 200 L186 200 M178 240 L186 240 M274 200 L282 200 M274 240 L282 240" strokeWidth="0.6" opacity="0.6" />
      {/* Tick marks above coin (counting) */}
      <path d="M30 70 L60 70 M30 80 L52 80 M30 90 L60 90" strokeWidth="0.7" opacity="0.55" />
    </>,
  );

const Priorities: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Compass-like wheel */}
      <circle cx="160" cy="160" r="100" strokeWidth="0.8" opacity="0.55" />
      <circle cx="160" cy="160" r="78" strokeWidth="0.6" opacity="0.6" />
      <circle cx="160" cy="160" r="4" fill="currentColor" stroke="none" />
      {Array.from({ length: 12 }, (_, i) => i).map((i) => {
        const a = (Math.PI * 2 * i) / 12 - Math.PI / 2;
        const r1 = 78;
        const r2 = i % 3 === 0 ? 100 : 90;
        const x1 = 160 + r1 * Math.cos(a);
        const y1 = 160 + r1 * Math.sin(a);
        const x2 = 160 + r2 * Math.cos(a);
        const y2 = 160 + r2 * Math.sin(a);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth="0.8" />;
      })}
      {/* Three priority needles */}
      <path d="M160 160 L160 80" strokeWidth="1.4" />
      <path d="M160 160 L228 196" strokeWidth="1.1" opacity="0.85" />
      <path d="M160 160 L100 210" strokeWidth="0.9" opacity="0.7" />
      {/* Stars marking top points */}
      <path d="M160 70 l3 7 7 1 -5 5 1 7 -6 -4 -6 4 1 -7 -5 -5 7 -1 z" strokeWidth="0.6" opacity="0.7" />
    </>,
  );

const Income: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Laptop */}
      <rect x="60" y="110" width="160" height="100" rx="4" strokeWidth="1" />
      <path d="M40 220 L240 220 L228 240 L52 240 Z" strokeWidth="1" />
      <path d="M120 230 L160 230" strokeWidth="0.7" opacity="0.7" />
      {/* Chart inside */}
      <path d="M76 188 L100 170 L120 178 L150 150 L180 158 L200 134" strokeWidth="1.1" />
      <circle cx="100" cy="170" r="2" fill="currentColor" stroke="none" />
      <circle cx="150" cy="150" r="2" fill="currentColor" stroke="none" />
      <circle cx="200" cy="134" r="2" fill="currentColor" stroke="none" />
      <path d="M76 200 L210 200" strokeWidth="0.5" opacity="0.55" />
      <path d="M76 122 L76 200" strokeWidth="0.5" opacity="0.55" />
      {/* Coffee cup right */}
      <path d="M250 200 L250 240 q0 12 14 12 h14 q14 0 14 -12 L292 200 Z" strokeWidth="1" />
      <path d="M292 210 q14 0 14 12 q0 12 -14 12" strokeWidth="0.8" />
      <path d="M260 190 q-2 -10 4 -16 M270 190 q-2 -10 4 -16 M280 190 q-2 -10 4 -16" strokeWidth="0.7" opacity="0.6" />
      {/* Currency tag */}
      <text
        x="270"
        y="100"
        textAnchor="middle"
        fontFamily="ui-monospace,Menlo,monospace"
        fontSize="14"
        fill="currentColor"
        stroke="none"
        opacity="0.65"
      >
        €$
      </text>
    </>,
  );

const Family: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Adult 1 */}
      <circle cx="90" cy="120" r="16" />
      <path d="M90 138 L90 220 M70 160 L110 160 M76 220 L70 270 M104 220 L110 270" />
      {/* Adult 2 */}
      <circle cx="160" cy="120" r="16" />
      <path d="M160 138 L160 220 M140 160 L180 160 M146 220 L140 270 M174 220 L180 270" />
      {/* Child */}
      <circle cx="220" cy="148" r="12" />
      <path d="M220 162 L220 220 M204 180 L236 180 M210 220 L204 260 M230 220 L236 260" />
      {/* Pet (dog silhouette) */}
      <path d="M260 250 q4 -16 18 -18 q4 -8 12 -6 q4 0 4 6 q10 2 12 12 q2 14 -10 18 L262 262 Z" strokeWidth="0.9" />
      <circle cx="296" cy="240" r="1.5" fill="currentColor" stroke="none" />
      <path d="M290 244 q-2 2 -4 0" strokeWidth="0.7" />
      {/* Holding-hands line */}
      <path d="M106 200 L142 200" strokeWidth="0.8" opacity="0.6" />
      <path d="M176 200 L210 200" strokeWidth="0.8" opacity="0.6" />
      {/* Ground line */}
      <path d="M40 270 L280 270" strokeWidth="0.6" opacity="0.5" strokeDasharray="2 4" />
    </>,
  );

const Languages: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Speech bubble 1 (back) */}
      <path d="M40 80 q0 -16 16 -16 h120 q16 0 16 16 v50 q0 16 -16 16 h-90 l-22 18 L72 146 H56 q-16 0 -16 -16 Z" strokeWidth="1" />
      <text
        x="96"
        y="120"
        fontFamily="serif"
        fontSize="32"
        fill="currentColor"
        stroke="none"
        opacity="0.8"
      >
        A
      </text>
      <text
        x="128"
        y="120"
        fontFamily="serif"
        fontSize="32"
        fill="currentColor"
        stroke="none"
        opacity="0.55"
      >
        ñ
      </text>
      {/* Speech bubble 2 (overlapping right) */}
      <path d="M150 160 q0 -16 16 -16 h110 q16 0 16 16 v50 q0 16 -16 16 h-78 l-20 18 L182 226 h-16 q-16 0 -16 -16 Z" strokeWidth="1" />
      <text
        x="194"
        y="200"
        fontFamily="serif"
        fontSize="28"
        fill="currentColor"
        stroke="none"
        opacity="0.8"
      >
        П
      </text>
      <text
        x="226"
        y="200"
        fontFamily="serif"
        fontSize="28"
        fill="currentColor"
        stroke="none"
        opacity="0.55"
      >
        漢
      </text>
      {/* Smaller bubble bottom-left */}
      <path d="M40 230 q0 -10 10 -10 h60 q10 0 10 10 v24 q0 10 -10 10 h-44 l-12 12 L60 264 h-10 q-10 0 -10 -10 Z" strokeWidth="0.9" />
      <text
        x="80"
        y="252"
        textAnchor="middle"
        fontFamily="serif"
        fontSize="20"
        fill="currentColor"
        stroke="none"
        opacity="0.7"
      >
        あ
      </text>
    </>,
  );

const Dealbreakers: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Caution triangle */}
      <path d="M160 60 L260 230 L60 230 Z" strokeWidth="1.1" />
      <path d="M160 110 L160 180" strokeWidth="1.4" />
      <circle cx="160" cy="200" r="3" fill="currentColor" stroke="none" />
      {/* Seismograph line below */}
      <path
        d="M20 270 L60 270 L70 260 L80 280 L92 254 L106 286 L120 264 L132 274 L144 266 L160 270 L180 270 L196 256 L210 284 L222 262 L236 280 L250 268 L300 270"
        strokeWidth="0.9"
        opacity="0.75"
      />
      {/* Dashed margin top */}
      <path d="M40 40 L280 40" strokeDasharray="2 6" opacity="0.4" />
      {/* X mark accent corner */}
      <g transform="translate(280 70)" opacity="0.6">
        <line x1="-10" y1="-10" x2="10" y2="10" strokeWidth="1.1" />
        <line x1="-10" y1="10" x2="10" y2="-10" strokeWidth="1.1" />
      </g>
    </>,
  );

const VisaPathway: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Branching paths converging on a single door */}
      <path d="M40 60 L150 200 L280 60" strokeWidth="1.1" />
      <path d="M40 200 L150 60 L280 200" strokeWidth="1" opacity="0.5" />
      {/* Door silhouette at center */}
      <rect x="135" y="200" width="30" height="55" strokeWidth="1.1" />
      <circle cx="158" cy="228" r="1.5" fill="currentColor" stroke="none" />
      {/* Visa-stamp accent corner */}
      <g transform="translate(265 75)" opacity="0.6">
        <rect x="-10" y="-10" width="20" height="20" strokeWidth="1" />
        <path d="M-7 -7 L7 7 M-7 7 L7 -7" strokeWidth="0.7" />
      </g>
    </>,
  );

const IncomeAmount: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Stack of coins growing right */}
      <ellipse cx="60" cy="240" rx="20" ry="6" strokeWidth="1" />
      <ellipse cx="120" cy="220" rx="22" ry="6" strokeWidth="1" />
      <ellipse cx="180" cy="195" rx="24" ry="6.5" strokeWidth="1.1" />
      <ellipse cx="240" cy="160" rx="26" ry="7" strokeWidth="1.2" />
      {/* € sign accent */}
      <text x="160" y="100" fontFamily="serif" fontSize="44" fill="currentColor" stroke="none" opacity="0.7">€</text>
    </>,
  );

const TaxResidency: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Two flags on either side of a globe */}
      <circle cx="160" cy="160" r="50" strokeWidth="1.1" />
      <path d="M110 160 L210 160 M160 110 L160 210" strokeWidth="0.8" opacity="0.6" />
      <path d="M120 130 Q160 145 200 130" strokeWidth="0.7" opacity="0.6" fill="none" />
      <path d="M120 190 Q160 175 200 190" strokeWidth="0.7" opacity="0.6" fill="none" />
      {/* Left flag */}
      <line x1="60" y1="240" x2="60" y2="120" strokeWidth="1" />
      <path d="M60 120 L100 130 L60 140 Z" strokeWidth="1" />
      {/* Right flag */}
      <line x1="260" y1="240" x2="260" y2="120" strokeWidth="1" />
      <path d="M260 120 L220 130 L260 140 Z" strokeWidth="1" />
      {/* Arrow between */}
      <path d="M85 250 L235 250" strokeWidth="0.9" strokeDasharray="3 4" />
    </>,
  );

const Ties: BackdropSvg = () =>
  baseSvg(
    <>
      {/* Two figure silhouettes linked by a tie/loop */}
      <circle cx="100" cy="120" r="14" strokeWidth="1" />
      <path d="M85 145 L85 200 L115 200 L115 145" strokeWidth="1" />
      <circle cx="220" cy="120" r="14" strokeWidth="1" />
      <path d="M205 145 L205 200 L235 200 L235 145" strokeWidth="1" />
      {/* Tie / link between them */}
      <path d="M115 170 Q160 140 205 170" strokeWidth="1.2" fill="none" />
      {/* Heart accent at link mid */}
      <path d="M155 162 Q160 158 165 162 Q170 158 165 168 Q160 173 155 168 Q150 158 155 162 Z" strokeWidth="0.8" fill="currentColor" opacity="0.55" />
    </>,
  );

const BuyProperty: BackdropSvg = () =>
  baseSvg(
    <>
      {/* House silhouette */}
      <path d="M80 220 L160 130 L240 220 Z" strokeWidth="1.2" />
      <path d="M115 220 L115 175 L205 175 L205 220" strokeWidth="1.1" />
      {/* Door + window */}
      <path d="M150 220 L150 195 L170 195 L170 220" strokeWidth="1" />
      <rect x="125" y="183" width="14" height="10" strokeWidth="0.9" />
      {/* Key-tag accent corner */}
      <g transform="translate(260 70)" opacity="0.65">
        <circle cx="0" cy="0" r="8" strokeWidth="1" />
        <line x1="6" y1="6" x2="22" y2="22" strokeWidth="1" />
      </g>
      {/* Dotted footing */}
      <path d="M40 250 L280 250" strokeDasharray="2 6" opacity="0.4" />
    </>,
  );

const BACKDROPS: Record<QuestionId, BackdropSvg> = {
  horizon: Horizon,
  passport: Passport,
  visa_pathway: VisaPathway,
  climate: Climate,
  urban_density: UrbanDensity,
  budget: Budget,
  priorities: Priorities,
  income: Income,
  income_amount: IncomeAmount,
  tax_residency_intent: TaxResidency,
  family: Family,
  // cost_of_living.json (2026-08-18) — reuses the Family backdrop; it's a
  // direct follow-up to `family` (same "who's relocating" visual theme).
  // Dedicated artwork not warranted for a single gated follow-up question.
  family_children_count: Family,
  languages: Languages,
  dealbreakers: Dealbreakers,
  ties: Ties,
  buy_property: BuyProperty,
  // CV.PoL — career_intent reuses TaxResidency backdrop (conceptually adjacent
  // intent-question). Dedicated artwork deferred until M1+.
  career_intent: TaxResidency,
};

export interface QuestionBackdropProps {
  questionId: QuestionId;
  className?: string;
}

/**
 * Decorative associative illustration for the current quiz question.
 * Cross-fades when the question id changes. Sits behind the form,
 * pointer-events-none, aria-hidden — purely atmospheric.
 */
export function QuestionBackdrop({ questionId, className }: QuestionBackdropProps) {
  const [shown, setShown] = useState(questionId);
  const [fading, setFading] = useState(false);
  const prefersReducedMotion = useRef(false);

  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
  }, []);

  useEffect(() => {
    if (questionId === shown) return;
    if (prefersReducedMotion.current) {
      setShown(questionId);
      return;
    }
    setFading(true);
    const id = window.setTimeout(() => {
      setShown(questionId);
      setFading(false);
    }, 180);
    return () => window.clearTimeout(id);
  }, [questionId, shown]);

  const Svg = BACKDROPS[shown];
  if (!Svg) return null;

  return (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-0 flex items-center justify-end overflow-hidden text-ink-700',
        className,
      )}
    >
      <div
        className="relative h-[420px] w-[420px] max-w-[55%] translate-x-[8%] opacity-[0.085] transition-opacity duration-300 motion-reduce:transition-none sm:opacity-[0.10]"
        style={{
          opacity: fading ? 0 : undefined,
          maskImage:
            'radial-gradient(circle at 70% 50%, rgba(0,0,0,1) 38%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0) 100%)',
          WebkitMaskImage:
            'radial-gradient(circle at 70% 50%, rgba(0,0,0,1) 38%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0) 100%)',
        }}
      >
        <Svg />
      </div>
    </div>
  );
}
